import { Navigate, Outlet } from "react-router-dom";
import { BreadcrumbsRoute } from "use-react-router-breadcrumbs";

import { AppConfig } from "~/AppConfig";
import SiteSpeed from "~/components/siteSpeed/SiteSpeed";
import { FEATURE_FLAGS, FeatureGate } from "~/featureFlags";
import GoogleDashboardPage from "~/pages/GoogleDashboardPage/GoogleDashboardPage";
import BuiltLinksReports from "~/pages/dashboard/reports/builtLinksReports/BuiltLinksReports";
import ClientReports from "~/pages/dashboard/reports/clientReports/ClientReports";
import KeywordRankingReports from "~/pages/dashboard/reports/keywordRankingReports/KeywordRankingReports";
import RecommendationExports from "~/pages/dashboard/reports/recommendationExports/RecommendationExports";
import SiteSalesReports from "~/pages/dashboard/reports/siteSalesReports/SiteSalesReports";
import WorkDoneReports from "~/pages/dashboard/reports/workDoneReports/WorkDoneReports";
import AboutPage from "~/pages/dashboard/siteSpeed/AboutPage";
import SiteSpeedDomainPage from "~/pages/dashboard/siteSpeed/SiteSpeedDomainPage";
import VisibilityEngineMetricsPage from "~/pages/dashboard/visibilityEngine/VisibilityEngineMetricsPage";
import VisibilityEngineMetricsPreview from "~/pages/dashboard/visibilityEngine/VisibilityEngineMetricsPreview";
import VisibilityEnginePage from "~/pages/dashboard/visibilityEngine/VisibilityEnginePage";
import VisibilityEngineSettingsPage from "~/pages/dashboard/visibilityEngine/VisibilityEngineSettingsPage";

import AnalyticsPage from "../pages/dashboard/AnalyticsPage";
import PagesPage from "../pages/dashboard/PagesPage";
import CustomizationsPage from "../pages/dashboard/customizationsPage/CustomizationsPage";
import InstallPage from "../pages/dashboard/installPage/InstallPage";
import InterlinksPage from "../pages/dashboard/interLinks/InterlinksPage";
import BrandLinkDetailPage from "../pages/dashboard/links/BrandLinkDetailPage";
import BrandLinksPage from "../pages/dashboard/links/BrandLinksPage";
import LinksOverview from "../pages/dashboard/links/LinksOverviewPage";
import NicheLinkDetailPage from "../pages/dashboard/links/NicheLinkDetailPage";
import NicheLinksPage from "../pages/dashboard/links/NicheLinksPage";
import OutreachLinkDetailPage from "../pages/dashboard/links/OutreachLinkDetailPage";
import OutreachLinksPage from "../pages/dashboard/links/OutreachLinksPage";
import OnPagePage from "../pages/dashboard/onPage/OnPagePage";
import SiteSettingsPage from "../pages/dashboard/siteSettings/SiteSettingsPage";
import KeywordPage from "../pages/keyword/KeywordPage";
import KeywordImportPage from "../pages/keywordImport/KeywordImportPage";
import KeywordImportsPage from "../pages/keywordImport/KeywordImportsPage";
import KeywordsPage from "../pages/keywords/KeywordsPage";

const SITE_ID = ":id";
export const SITE_PATTERNS = {
  site: SITE_ID,

  settings: `${SITE_ID}/settings`,
  pages: `${SITE_ID}/pages`,
  visibilityMetrics: `${SITE_ID}/visibility-engine/metrics`,
  visibilityEngineSettings: `${SITE_ID}/visibility-engine/settings`,
  visibilityEngineSettingsTab: `${SITE_ID}/visibility-engine/settings/:tabId`,
  googleDashboard: `${SITE_ID}/google-dashboard`,
  trafficMetrics: `${SITE_ID}/visibility-engine/traffic`,
  legacyTrafficMetrics: `${SITE_ID}/visibility-engine`,

  visibilityEngineDomain: `${SITE_ID}/visibility-engine/domains/:domainId`,
  visibilityEngineAbout: `${SITE_ID}/visibility-engine/about`,

  liveEditor: `${SITE_ID}/live-editor`,
  install: "install",
  customizations: "customizations",

  links: `${SITE_ID}/links`,
  overview: "overview",
  nicheLinks: "niche-links",
  nicheTask: "niche-links/:taskId",
  outreachLinks: "outreach-links",
  outreachTask: "outreach-links/:taskId",
  brandLinks: "brand-links",
  brandTask: "brand-links/:taskId",

  keywords: "keywords",
  keyword: "keywords/:keywordId",
  keywordImports: "keywords/imports",
  keywordImport: "keywords/imports/:importId",

  reports: `${SITE_ID}/reports`,
  builtLinks: "built-links",
  keywordRanking: "keyword-ranking",
  siteSales: "site-sales",
  recommendationExports: "recommendation-exports",
  workDone: "work-done",
  client: "client",

  onpage: `${SITE_ID}/onpage`,
  content: `${SITE_ID}/content`,
  schema: `${SITE_ID}/schema`,
  aiInstructions: `${SITE_ID}/ai-instructions`,
  interlinks: `${SITE_ID}/interlinks`,
} as const;

// Single source of truth for the Visibility Engine domains wizard steps.
// Consumed both here (site context) and in `routes.tsx` (global context) so the
// two route trees can't drift apart.
export const VISIBILITY_ENGINE_WIZARD_STEPS = [
  { step: "setup", breadcrumb: "Setup" },
  { step: "verification", breadcrumb: "Verification" },
  { step: "certificate", breadcrumb: "Certificate" },
  { step: "dns", breadcrumb: "DNS" },
  { step: "ns", breadcrumb: "NS" },
  { step: "routing", breadcrumb: "Routing" },
  { step: "confirmation", breadcrumb: "Edge Instructions" },
  { step: "install", breadcrumb: "Install" },
  { step: "configure", breadcrumb: "Configure" },
] as const;

/**
 * Every path starts with ":id"
 * Keeps nested groups only where you need Outlet (live-editor, links, keywords, reports)
 */
export const siteRoutes: BreadcrumbsRoute[] = [
  {
    path: SITE_PATTERNS.site,
    breadcrumb: "Site",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_ANALYTICS} redirectToRoot>
        <AnalyticsPage />
      </FeatureGate>
    ),
  },

  {
    path: SITE_PATTERNS.settings,
    breadcrumb: "Site Settings",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_SETTINGS_GENERAL}
        redirectToRoot
      >
        <SiteSettingsPage />
      </FeatureGate>
    ),
    children: [
      {
        path: ":tabId",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_SETTINGS_GENERAL}
            redirectToRoot
          >
            <></>
          </FeatureGate>
        ),
      },
    ],
  },

  {
    path: SITE_PATTERNS.pages,
    breadcrumb: "Pages",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_PAGES} redirectToRoot>
        <PagesPage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.visibilityMetrics,
    breadcrumb: "Visibility Metrics",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_VISIBILITY_ENGINE}
        redirectToRoot
      >
        <VisibilityEngineMetricsPage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.visibilityEngineSettings,
    breadcrumb: "AI Visibility Engine",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_SETTINGS_PRERENDERING}
        redirectToRoot
      >
        <VisibilityEngineSettingsPage />
      </FeatureGate>
    ),
  },
  {
    // Tabbed sub-routes of the AI Visibility Engine settings page (e.g. the
    // Firewall tab). The page reads the active tab from the URL, mirroring the
    // Site Settings module.
    path: SITE_PATTERNS.visibilityEngineSettingsTab,
    breadcrumb: "AI Visibility Engine",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_SETTINGS_PRERENDERING}
        redirectToRoot
      >
        <VisibilityEngineSettingsPage />
      </FeatureGate>
    ),
  },
  // DEV-ONLY visual sandbox for the before/after styling. The route is only
  // registered in development (AppConfig.IS_DEV), so it's never reachable in
  // production.
  ...(AppConfig.IS_DEV
    ? [
        {
          path: `${SITE_PATTERNS.visibilityMetrics}/preview`,
          element: <VisibilityEngineMetricsPreview />,
        },
      ]
    : []),
  {
    path: SITE_PATTERNS.liveEditor,
    breadcrumb: "Live Editor",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_ENHANCEMENTS} redirectToRoot>
        <Outlet />
      </FeatureGate>
    ),
    children: [
      {
        path: SITE_PATTERNS.install,
        breadcrumb: "Install",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_ENHANCEMENTS_INSTALL}
            redirectToRoot
          >
            <InstallPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.customizations,
        breadcrumb: "Customizations",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_ENHANCEMENTS}
            redirectToRoot
          >
            <CustomizationsPage group="enhancement" />
          </FeatureGate>
        ),
      },
      {
        path: "",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_ENHANCEMENTS}
            redirectToRoot
          >
            <Navigate to={SITE_PATTERNS.customizations} replace />
          </FeatureGate>
        ),
      },
    ],
  },

  {
    path: SITE_PATTERNS.links,
    breadcrumb: "Links",
    element: <Outlet />,
    children: [
      {
        path: SITE_PATTERNS.overview,
        breadcrumb: "Overview",
        element: <LinksOverview />,
      },
      {
        path: SITE_PATTERNS.nicheLinks,
        breadcrumb: "Authority Links",
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_LINKS_NICHE} redirectToRoot>
            <NicheLinksPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.nicheTask,
        breadcrumb: "Authority Link Details",
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_LINKS_NICHE} redirectToRoot>
            <NicheLinkDetailPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.outreachLinks,
        breadcrumb: "Community Links",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_LINKS_OUTREACH}
            redirectToRoot
          >
            <OutreachLinksPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.outreachTask,
        breadcrumb: "Community Link Details",
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_LINKS_OUTREACH}
            redirectToRoot
          >
            <OutreachLinkDetailPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.brandLinks,
        breadcrumb: "Foundation Links",
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_LINKS_BRAND} redirectToRoot>
            <BrandLinksPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.brandTask,
        breadcrumb: "Foundation Link Details",
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_LINKS_BRAND} redirectToRoot>
            <BrandLinkDetailPage />
          </FeatureGate>
        ),
      },
    ],
  },

  {
    path: SITE_PATTERNS.site,
    breadcrumb: "Keyword List",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_KEYWORDS} redirectToRoot>
        <Outlet />
      </FeatureGate>
    ),
    children: [
      {
        path: SITE_PATTERNS.keywords,
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_KEYWORDS} redirectToRoot>
            <KeywordsPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.keyword,
        element: (
          <FeatureGate feature={FEATURE_FLAGS.SITES_KEYWORDS} redirectToRoot>
            <KeywordPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.keywordImports,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_KEYWORD_IMPORTS}
            redirectToRoot
          >
            <KeywordImportsPage />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.keywordImport,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_KEYWORD_IMPORTS}
            redirectToRoot
          >
            <KeywordImportPage />
          </FeatureGate>
        ),
      },
    ],
  },

  {
    path: SITE_PATTERNS.reports,
    breadcrumb: "Reports",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_REPORTS} redirectToRoot>
        <Outlet />
      </FeatureGate>
    ),
    children: [
      {
        path: SITE_PATTERNS.builtLinks,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_BUILT_LINKS}
            redirectToRoot
          >
            <BuiltLinksReports />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.keywordRanking,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_KEYWORD_RANKINGS}
            redirectToRoot
          >
            <KeywordRankingReports />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.siteSales,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_SITE_SALES}
            redirectToRoot
          >
            <SiteSalesReports />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.recommendationExports,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_RECOMMENDATION_EXPORTS}
            redirectToRoot
          >
            <RecommendationExports />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.workDone,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_WORK_DONE}
            redirectToRoot
          >
            <WorkDoneReports />
          </FeatureGate>
        ),
      },
      {
        path: SITE_PATTERNS.client,
        element: (
          <FeatureGate
            feature={FEATURE_FLAGS.SITES_REPORTS_CLIENT}
            redirectToRoot
          >
            <ClientReports />
          </FeatureGate>
        ),
      },
    ],
  },

  {
    path: SITE_PATTERNS.onpage,
    breadcrumb: "On Page",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_RECOMMENDATIONS} redirectToRoot>
        <OnPagePage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.content,
    breadcrumb: "Content",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_CUSTOMIZATIONS} redirectToRoot>
        <CustomizationsPage group="ai" />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.schema,
    breadcrumb: "Schema",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_SCHEMA} redirectToRoot>
        <CustomizationsPage group="schema" />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.aiInstructions,
    breadcrumb: "AI Instructions",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_AI_INSTRUCTIONS} redirectToRoot>
        <CustomizationsPage group="ai_instructions" />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.interlinks,
    breadcrumb: "Interlinks",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.SITES_INTERLINKS} redirectToRoot>
        <InterlinksPage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.googleDashboard,
    breadcrumb: "Google Dashboard",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_GOOGLE_DASHBOARD}
        redirectToRoot
      >
        <GoogleDashboardPage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.trafficMetrics,
    breadcrumb: "Traffic",
    element: (
      <FeatureGate
        feature={FEATURE_FLAGS.SITES_VISIBILITY_ENGINE}
        redirectToRoot
      >
        <VisibilityEnginePage />
      </FeatureGate>
    ),
  },
  {
    path: SITE_PATTERNS.legacyTrafficMetrics,
    element: <Navigate to="traffic" replace relative="path" />,
  },

  // Visibility Engine domains module mirrored into the site context. Mirrors
  // the global routes in `routes.tsx`; navigation stays site-scoped via
  // `useVisibilityEngineRoutes`. The domains list is global-only — in the site
  // context the module deals only with the site's own delegated domain.
  {
    path: SITE_PATTERNS.visibilityEngineDomain,
    element: (
      <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
        <SiteSpeed />
      </FeatureGate>
    ),
  },
  ...VISIBILITY_ENGINE_WIZARD_STEPS.map(({ step, breadcrumb }) => ({
    path: `${SITE_PATTERNS.visibilityEngineDomain}/wizard/${step}`,
    breadcrumb,
    element: (
      <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
        <SiteSpeedDomainPage />
      </FeatureGate>
    ),
  })),
  {
    path: SITE_PATTERNS.visibilityEngineAbout,
    breadcrumb: "About AI Visibility Engine",
    element: (
      <FeatureGate feature={FEATURE_FLAGS.DOMAINS} redirectToRoot>
        <AboutPage />
      </FeatureGate>
    ),
  },
];
